import { Link } from "react-router-dom";
import {
  Sparkle,
  Lock,
  Eye,
  Key,
  ShieldCheck,
  DownloadSimple,
  Bell,
  Heart,
} from "@phosphor-icons/react";

const SUPPORT_EMAIL = "support@claritymirror.app";

export default function PrivacyPromisePage() {
  return (
    <div className="min-h-screen bg-canvas text-ink">
      <nav className="px-5 sm:px-8 md:px-16 py-5 border-b border-line">
        <Link to="/" className="inline-flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-clay/90 grid place-items-center">
            <Sparkle weight="fill" className="text-canvas" size={12} />
          </div>
          <span className="font-serif-display text-xl">Clarity Mirror</span>
        </Link>
      </nav>

      <article className="max-w-2xl mx-auto px-5 sm:px-8 py-16" data-testid="privacy-promise-page">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-ink-mute">
          Our promise to you
        </p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 mb-5 leading-[1.05]">
          How we protect you.
        </h1>
        <p className="text-ink-soft text-base md:text-lg leading-relaxed">
          You’re about to write down things you may not have said out loud to anyone.
          That deserves a straight answer about what happens to those words.
        </p>
        <p className="text-ink-soft text-base leading-relaxed mt-4">
          So here it is. No legal fog. Just the truth, in plain English.
        </p>

        <Block
          icon={<Lock size={20} weight="thin" />}
          title="Your writing stays yours"
          testid="promise-ownership"
        >
          <p>
            What you write belongs to you. Not to us.
          </p>
          <p>
            We do not sell your entries. We never will. We don’t hand your
            moods, your cycle data, or your body signals to advertisers. We
            don’t work with advertisers at all. There are no trackers, no ad
            pixels, and no data brokers anywhere in this app.
          </p>
          <p>
            And your writing is never used to train an AI model. Not Anthropic’s.
            Not ours. Not anyone’s. Your journal is not raw material for a machine.
            It’s your journal.
          </p>
        </Block>

        <Block
          icon={<Eye size={20} weight="thin" />}
          title="Who actually sees your data"
          testid="promise-third-parties"
        >
          <p>
            Being vague here would be easy. We’d rather be specific.
          </p>
          <p>
            Three companies touch your data, and each one only touches the part
            it needs to do its job:
          </p>
          <ul>
            <li>
              <b>Anthropic</b>, the company behind Claude, the AI that writes
              your reflections. When you ask for a reflection, that entry is sent
              to their API over an encrypted connection, read by the model, and
              answered. Anthropic’s terms say API data is not used to train their
              models. They don’t sell it.
            </li>
            <li>
              <b>Stripe</b> handles payment, and only payment. They see your
              name, your email, and your card details when you subscribe. We never
              see your card number. Stripe doesn’t see a single word you’ve
              written. They don’t sell your data.
            </li>
            <li>
              <b>Emergent</b> is our hosting provider. They run the servers this
              app lives on, so your account and entries are stored on their
              infrastructure. That’s the same relationship every app has with its
              host. They don’t sell your data.
            </li>
          </ul>
          <p>
            That’s the whole list. Nobody else. If that ever changes, we’ll
            tell you before it does, not after.
          </p>
        </Block>

        <Block
          icon={<Key size={20} weight="thin" />}
          title="Your password"
          testid="promise-password"
        >
          <p>
            Nobody at Clarity Mirror can read your password. Not us, not an
            engineer, not anyone with database access.
          </p>
          <p>
            The moment you type it, it’s scrambled into a one-way code
            (bcrypt, with a unique salt just for you). We only ever store the
            scrambled version. When you log in, we scramble what you typed and
            check if the two match. The real password is never written down
            anywhere.
          </p>
          <p>
            Which is also why we can’t email it back to you if you forget it.
            We genuinely don’t have it.
          </p>
        </Block>

        <Block
          icon={<ShieldCheck size={20} weight="thin" />}
          title="Your entries are encrypted"
          testid="promise-encryption"
        >
          <p>
            Your writing is protected in two different moments.
          </p>
          <p>
            <b>While it’s moving.</b> Everything travelling between your phone
            or laptop and our servers goes through an encrypted tunnel (HTTPS/TLS).
            Nobody sitting on your coffee-shop wifi can read it on the way.
          </p>
          <p>
            <b>While it’s sitting still.</b> Before your entry is saved, we
            encrypt the text itself, and the reflection written about it.
            What sits in our database is scrambled code, not sentences. Anyone
            looking at the raw storage, on any host, sees nothing readable.
          </p>
          <p>
            Your entries are encrypted with a key unique to your account.
          </p>
        </Block>

        <Block
          icon={<DownloadSimple size={20} weight="thin" />}
          title="You are in control"
          testid="promise-control"
        >
          <p>
            You can walk out with everything, or erase everything. Both take about
            ten seconds, and both live in Settings.
          </p>
          <ul>
            <li>
              <b>Take it all with you.</b> Download a single file containing every
              entry you’ve ever written, every mood, every body signal, and every
              reflection. It’s yours to keep, forever.
            </li>
            <li>
              <b>Erase it all.</b> Delete your account and every trace of your data,
              permanently. No waiting period, no “are you sure” loop, no
              hidden copy kept in a drawer somewhere. Gone means gone.
            </li>
          </ul>
          <p>
            You don’t need to ask us for permission, and you don’t need to
            explain why.
          </p>
        </Block>

        <Block
          icon={<Bell size={20} weight="thin" />}
          title="If something goes wrong"
          testid="promise-breach"
        >
          <p>
            We hope this never happens. But if it does, you’ll hear it from us
            first.
          </p>
          <p>
            If there is ever a security incident that affects your data, we will
            email you within <b>72 hours</b> of confirming it. We’ll tell you
            exactly what happened, exactly what was affected, and exactly what
            we’re doing about it. No hiding it in a blog post. No quiet update to
            a policy page.
          </p>
        </Block>

        <Block
          icon={<Heart size={20} weight="thin" />}
          title="We are not a therapy app"
          testid="promise-not-therapy"
          accent
        >
          <p>
            This matters, so we’ll say it clearly.
          </p>
          <p>
            Clarity Mirror is a reflection tool. It helps you notice your own
            patterns. It is <b>not</b> therapy, not a mental health service, and not
            a replacement for a real human professional who knows you. The AI
            isn’t diagnosing you. It’s just holding up a mirror.
          </p>
          <p>
            If you’re in crisis, please reach out to someone right now.
          </p>
          <ul>
            <li>
              <b>In the US:</b> call or text <b>988</b> (Suicide &amp; Crisis Lifeline),
              available 24/7.
            </li>
            <li>
              <b>Anywhere else:</b> call your local emergency number, or your
              country’s crisis line.
            </li>
          </ul>
          <p>You deserve real support. Please go get it.</p>
        </Block>

        <div className="mt-16 pt-10 border-t border-line">
          <h2 className="font-serif-display text-2xl mb-3">Still have a question?</h2>
          <p className="text-ink-soft leading-relaxed">
            Ask us anything about your data and we’ll answer honestly. Email{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              data-testid="privacy-promise-email"
              className="text-clay hover:text-clay-dark underline underline-offset-4 break-words"
            >
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
          <p className="text-sm text-ink-mute leading-relaxed mt-6">
            This page is the human version. The formal versions are the{" "}
            <Link
              to="/terms"
              data-testid="promise-to-terms-link"
              className="text-clay hover:text-clay-dark underline underline-offset-4"
            >
              user agreement &amp; privacy terms
            </Link>{" "}
            and the{" "}
            <Link
              to="/terms-of-service"
              data-testid="promise-to-tos-link"
              className="text-clay hover:text-clay-dark underline underline-offset-4"
            >
              Terms of Service
            </Link>
            . If those two ever seem to contradict this page, the legal documents
            are what govern, but we’ve tried hard to make sure they say the
            same thing.
          </p>
        </div>
      </article>
    </div>
  );
}

function Block({ icon, title, children, testid, accent = false }) {
  return (
    <section
      data-testid={testid}
      className={`mt-14 rounded-3xl border p-6 sm:p-8 ${
        accent ? "bg-clay/5 border-clay/30" : "bg-canvas-warm border-line"
      }`}
    >
      <div className="flex items-start gap-3">
        <span className={`shrink-0 mt-1 ${accent ? "text-clay-dark" : "text-clay"}`}>{icon}</span>
        <h2 className="font-serif-display text-2xl md:text-3xl leading-tight">{title}</h2>
      </div>
      <div className="mt-5 text-ink-soft leading-relaxed space-y-4 [&_ul]:list-none [&_ul]:pl-0 [&_ul]:space-y-3 [&_li]:relative [&_li]:pl-5 [&_li:before]:content-['·'] [&_li:before]:absolute [&_li:before]:left-0 [&_li:before]:text-clay [&_li:before]:text-xl [&_b]:text-ink [&_b]:font-semibold">
        {children}
      </div>
    </section>
  );
}
